"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import Cookies from "js-cookie";
import { useRouter } from "next/navigation";
import {
    FileText,
    Wrench,
    Scroll,
    CreditCard,
    Building2,
    X,
} from "lucide-react";

const services = [
    {
        title: "Rental Agreement",
        icon: FileText,
        link: "/rental-agreement",
        bgColor: "bg-[#eef7ff]",
        borderColor: "border-[#d0e8ff]",
        textColor: "text-[#0066cc]",
        iconColor: "text-[#0066cc]",
    },
    {
        title: "Civil Services",
        icon: Wrench,
        link: "/become-civil-suppliers",
        bgColor: "bg-[#eefaf2]",
        borderColor: "border-[#d0f1dc]",
        textColor: "text-[#1e7e34]",
        iconColor: "text-[#1e7e34]",
    },
    {
        title: "Lease Agreement",
        icon: Scroll,
        link: "/lease-agreement",
        bgColor: "bg-[#fff8ee]",
        borderColor: "border-[#ffe8cc]",
        textColor: "text-[#d97706]",
        iconColor: "text-[#d97706]",
    },
    {
        title: "Business Card",
        icon: CreditCard,
        link: "/seller/dashboard",
        bgColor: "bg-[#fff0f6]",
        borderColor: "border-[#ffd6e8]",
        textColor: "text-[#d63384]",
        iconColor: "text-[#d63384]",
    },
];

const serviceDetails = {
    "Rental Agreement": {
        subtitle: "Create & Manage Agreements",
        tagline: "Draft customized, legally binding Rental Agreements online.",
        benefits: [
            "Quick online drafting without visiting registry offices or brokers.",
            "Customize clauses, rent amount, security deposit, and lock-in period easily.",
            "100% legal & customizable formatting designed by real estate legal experts.",
            "Manage all your active and draft agreements securely in your dashboard."
        ],
        authNotice: "Accessing this feature requires your DreamAcres account. Please log in to create new agreements or view your existing drafts.",
        themeColor: "text-[#0066cc]",
        themeBg: "bg-[#eef7ff]",
        themeBorder: "border-[#d0e8ff]",
        themeBtn: "bg-[#0066cc] hover:bg-[#0052a3]"
    },
    "Civil Services": {
        subtitle: "Register & Publish Services",
        tagline: "Publish your civil services and connect directly with local customers.",
        benefits: [
            "Register your profile and publish details about your civil and construction services.",
            "Allow local customers to discover your business easily and reach out to you directly.",
            "Showcase your portfolio, expertise, and contact details to generate direct sales.",
            "Expand your reach and grow your business with zero middleman commissions."
        ],
        authNotice: "Accessing this feature requires your DreamAcres account. Please log in to publish your civil services or explore local experts.",
        themeColor: "text-[#1e7e34]",
        themeBg: "bg-[#eefaf2]",
        themeBorder: "border-[#d0f1dc]",
        themeBtn: "bg-[#1e7e34] hover:bg-[#155a24]"
    },
    "Lease Agreement": {
        subtitle: "Create & Manage Agreements",
        tagline: "Draft long-term, customized Lease Agreements online.",
        benefits: [
            "Perfect for commercial properties, residential leases, and land agreements.",
            "Define rent escalation rates, renewal terms, and maintenance obligations clearly.",
            "Saves time and money compared to traditional stamp paper drafts.",
            "Manage and retrieve all your lease agreements in one place."
        ],
        authNotice: "Accessing this feature requires your DreamAcres account. Please log in to create new agreements or view your existing drafts.",
        themeColor: "text-[#d97706]",
        themeBg: "bg-[#fff8ee]",
        themeBorder: "border-[#ffe8cc]",
        themeBtn: "bg-[#d97706] hover:bg-[#b45309]"
    },
    "Business Card": {
        subtitle: "Generate Digital Business Cards",
        tagline: "Create and share your professional digital business card instantly.",
        benefits: [
            "Log in and design a custom, elegant digital business card in just a few minutes.",
            "Easily share your digital card with clients, family, and friends via any platform.",
            "Update your contact information, social links, and details anytime in real-time.",
            "Go paperless with a modern, eco-friendly digital alternative to physical cards."
        ],
        authNotice: "Accessing this feature requires your DreamAcres account. Please log in to design your digital business card and start sharing.",
        themeColor: "text-[#d63384]",
        themeBg: "bg-[#fff0f6]",
        themeBorder: "border-[#ffd6e8]",
        themeBtn: "bg-[#d63384] hover:bg-[#b8206c]"
    }
};

const ServicesSection = () => {
    const router = useRouter();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedService, setSelectedService] = useState(null);

    useEffect(() => {
        const token = Cookies.get('token');
        if (token) {
            setIsLoggedIn(true);
        }
    }, []);

    const postPropertyLink = isLoggedIn ? "/seller/post-property" : "/login";

    const handleServiceClick = (e, item) => {
        if (isLoggedIn) {
            router.push(item.link);
        } else {
            setSelectedService(item);
            setIsModalOpen(true);
        }
    };

    const details = selectedService ? serviceDetails[selectedService.title] : null;
    const SelectedIcon = selectedService ? selectedService.icon : null;

    return (
        <section className="w-full bg-white py-4">
            <div className="max-w-6xl mx-auto px-4 flex flex-col gap-4">

                {/* Only visible on mobile: Post Free Property banner */}
                <div className="block lg:hidden">
                    <Link
                        href={postPropertyLink}
                        className="flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-[#810A5F] to-[#A61382] text-white shadow-md hover:shadow-lg transition-all duration-300"
                    >
                        <div className="flex items-center gap-3">
                            {/* Icon with white/transparent circle and absolute + badge */}
                            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0 relative">
                                <Building2 className="w-6 h-6 text-white" />
                                <span className="absolute bottom-1 right-1 bg-white text-[#810A5F] rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold shadow-sm">
                                    +
                                </span>
                            </div>

                            <div>
                                <h3 className="font-bold text-base tracking-wide">
                                    Post Property
                                </h3>
                                <p className="text-white/80 text-xs mt-0.5 font-medium">
                                    Reach thousands of buyers & renters instantly
                                </p>
                            </div>
                        </div>

                        {/* Yellow "FREE" pill and white arrow */}
                        <div className="flex items-center gap-1.5 shrink-0">
                            <span className="bg-[#FFC72C] text-[#810A5F] text-[10px] font-extrabold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
                                Free
                            </span>
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </Link>
                </div>

                {/* Highlight Services Row */}
                <div className="grid grid-cols-4 gap-2 sm:gap-4 lg:flex lg:justify-center lg:gap-6 py-2">
                    {services.map((item, index) => {
                        const Icon = item.icon;

                        return (
                            <div
                                key={index}
                                onClick={(e) => handleServiceClick(e, item)}
                                className={`flex flex-col items-center justify-center p-2.5 sm:p-4 rounded-xl sm:rounded-2xl border transition-all duration-300 ${item.bgColor} ${item.borderColor} ${item.textColor} lg:w-[160px] lg:h-[120px] hover:scale-[1.02] hover:shadow-sm group cursor-pointer`}
                            >
                                {/* White circle containing the icon */}
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center mb-2 shadow-sm group-hover:scale-105 transition-transform">
                                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${item.iconColor}`} />
                                </div>

                                <span className="text-[10px] sm:text-xs font-bold text-gray-800 leading-tight">
                                    {item.title}
                                </span>
                            </div>
                        );
                    })}
                </div>

            </div>

            {/* Premium Details & Login Popup Modal */}
            {isModalOpen && selectedService && details && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4 text-center">
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                            onClick={() => setIsModalOpen(false)}
                        ></div>

                        {/* Modal Panel */}
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all border border-gray-100 flex flex-col p-6 animate-in fade-in zoom-in-95 duration-200">

                            {/* Header */}
                            <div className="flex justify-between items-start gap-4 mb-4">
                                <div className="flex items-center gap-3">
                                    <div className={`w-12 h-12 rounded-xl ${details.themeBg} border ${details.themeBorder} flex items-center justify-center shrink-0`}>
                                        <SelectedIcon className={`w-6 h-6 ${details.themeColor}`} />
                                    </div>
                                    <div className="text-left">
                                        <h3 className="text-lg font-bold text-gray-900 leading-tight">
                                            {selectedService.title}
                                        </h3>
                                        <p className="text-xs text-gray-500 font-medium">{details.subtitle}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-lg hover:bg-gray-100 shrink-0"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="w-full h-px bg-gray-100 my-2"></div>

                            {/* Tagline */}
                            <p className="text-sm font-semibold text-gray-700 mb-4 text-left">
                                {details.tagline}
                            </p>

                            {/* Benefits List */}
                            <ul className="space-y-3 mb-6">
                                {details.benefits.map((benefit, i) => (
                                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600 text-left">
                                        <span className={`mt-0.5 shrink-0 w-5 h-5 rounded-full ${details.themeBg} flex items-center justify-center`}>
                                            <svg className={`w-3 h-3 ${details.themeColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </span>
                                        <span className="leading-snug">{benefit}</span>
                                    </li>
                                ))}
                            </ul>

                            {/* Auth Requirement Notice */}
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 text-left mb-6 flex items-start gap-3">
                                <span className="text-base shrink-0 mt-0.5">🔒</span>
                                <p className="text-xs text-gray-500 font-medium leading-relaxed">
                                    {details.authNotice}
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="w-full sm:w-1/2 py-2.5 px-4 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <Link
                                    href={`/login?redirect=${encodeURIComponent(selectedService.link)}`}
                                    className={`w-full sm:w-1/2 py-2.5 px-4 rounded-xl ${details.themeBtn} text-white text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center`}
                                >
                                    Login to Proceed
                                </Link>
                            </div>

                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ServicesSection;